<template>
  <div class="amii-es-ia-page container py-4">
    <div class="text-center mb-4">
      <h1 class="h3 font-weight-bold">
        AMII es IA
      </h1>
      <p class="text-muted">
        Tu imagen generada con inteligencia artificial
      </p>
    </div>

    <div
      v-if="loading"
      class="text-center py-5"
    >
      <div class="spinner-border text-primary" />
    </div>

    <div
      v-else-if="!imageUrl"
      class="alert alert-warning text-center"
    >
      No encontramos esta imagen. Verificá el enlace.
    </div>

    <div
      v-else
      class="card shadow-sm p-4 mx-auto"
      style="max-width: 560px; border-radius: 12px;"
    >
      <div class="text-center mb-3">
        <img
          :src="imageUrl"
          alt="Imagen generada"
          class="img-fluid rounded"
          style="max-height: 480px;"
        >
      </div>

      <!-- COMPARTIR / DESCARGAR -->
      <button
        type="button"
        class="btn btn-success btn-block font-weight-bold py-2 mb-2
          d-flex align-items-center justify-content-center"
        style="border-radius: 20px;"
        :disabled="sharing"
        @click="shareImage"
      >
        <font-awesome-icon
          :icon="['fab', 'whatsapp']"
          class="mr-2"
        />
        <font-awesome-icon
          :icon="['fab', 'instagram']"
          class="mr-2"
        />
        <span>{{ sharing ? 'Compartiendo...' : 'Compartir' }}</span>
      </button>

      <button
        type="button"
        class="btn btn-outline-primary btn-block font-weight-bold py-2 mb-2"
        style="border-radius: 20px;"
        :disabled="downloading"
        @click="downloadImage"
      >
        {{ downloading ? 'Descargando...' : 'Descargar imagen' }}
      </button>

      <div
        v-if="actionMessage"
        class="alert alert-info small text-center py-2 mb-2"
      >
        {{ actionMessage }}
      </div>

      <p class="text-center small text-muted mb-4">
        Etiquetanos en Instagram como
        <a
          href="https://instagram.com/amii.app"
          target="_blank"
          rel="noopener noreferrer"
        >@amii.app</a>
      </p>

      <!-- CTA REGISTRO -->
      <div class="alert alert-primary text-center mb-4">
        <strong>¿Querés usar AMII en tu consulta?</strong>
        <p class="small mb-2">
          Gestioná historias clínicas, recetas y citas desde un solo lugar.
        </p>
        <a
          href="/#formulario-registro-medico"
          class="btn btn-primary font-weight-bold"
          style="border-radius: 20px;"
        >
          Registrarme gratis
        </a>
      </div>

      <!-- FORMULARIO OPCIONAL DE LEAD -->
      <div
        v-if="!leadSent"
        class="border-top pt-3"
      >
        <h2 class="h6 font-weight-bold mb-2">
          ¿Preferís que te contactemos?
        </h2>
        <p class="small text-muted mb-3">
          Dejanos tus datos y te escribimos con más información.
        </p>

        <div class="form-group mb-2">
          <input
            v-model="lead.name"
            type="text"
            class="form-control"
            placeholder="Nombre y apellido"
          >
        </div>
        <div class="form-group mb-2">
          <input
            v-model="lead.whatsapp"
            type="tel"
            class="form-control"
            placeholder="WhatsApp"
          >
        </div>
        <div class="form-group mb-2">
          <input
            v-model="lead.instagram"
            type="text"
            class="form-control"
            placeholder="Usuario de Instagram (opcional)"
          >
        </div>

        <div
          v-if="leadError"
          class="alert alert-danger small"
        >
          {{ leadError }}
        </div>

        <button
          class="btn btn-outline-primary btn-block font-weight-bold py-2"
          style="border-radius: 20px;"
          :disabled="sendingLead"
          @click="submitLead"
        >
          {{ sendingLead ? 'Enviando...' : 'Quiero que me contacten' }}
        </button>
      </div>

      <div
        v-else
        class="alert alert-success text-center mb-0"
      >
        ¡Gracias! Te vamos a contactar pronto.
      </div>
    </div>
  </div>
</template>

<script>
import gql from 'graphql-tag';

const PUBLIC_IMAGE_QUERY = gql`
  query PublicEventImage($id: ID!) {
    publicEventImage(id: $id) {
      id
      url
      specialty
    }
  }
`;

const LEAD_MUTATION = gql`
  mutation EventImageLeadSubmit(
    $imageId: ID!
    $lead: EventImageLeadInput!
  ) {
    eventImageLeadSubmit(
      imageId: $imageId
      lead: $lead
    ) {
      msg
      code
    }
  }
`;

export default {
  name: 'AmiiEsIA',
  data() {
    return {
      loading: true,
      imageUrl: '',
      specialty: '',
      lead: {name: '', whatsapp: '', instagram: ''},
      sendingLead: false,
      leadSent: false,
      leadError: '',
      sharing: false,
      downloading: false,
      actionMessage: '',
    };
  },
  computed: {
    multimediaRoot() {
      return process.env.NODE_ENV === 'production'
        ? process.env.VUE_APP_MULTIMEDIA_HTTPS
        : process.env.VUE_APP_MULTIMEDIA_HTTP;
    },
    imageId() {
      return this.$route.params.id;
    },
    // Raíz pública de las imágenes promocionales de evento.
    eventImagesRoot() {
      const root = this.multimediaRoot || '';
      return `${root.replace(/\/images\/?$/, '')}/event-images/`;
    },
    shareDescription() {
      return 'Usando la IA de @AMII.app en salud';
    },
    whatsappShareUrl() {
      const text = encodeURIComponent(
          `${this.shareDescription} ${window.location.href}`,
      );
      return `https://wa.me/?text=${text}`;
    },
  },
  mounted() {
    this.loadImage();
  },
  methods: {
    async fetchImageBlob() {
      const response = await fetch(this.imageUrl, {
        mode: 'cors',
      });
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }
      return await response.blob();
    },

    getImageFilename(extension = 'jpg') {
      const idPart = this.imageId ? `-${this.imageId}` : '';
      return `amii-ia${idPart}.${extension}`;
    },

    async shareImage() {
      if (!this.imageUrl || this.sharing) return;
      this.sharing = true;
      this.actionMessage = '';

      try {
        const blob = await this.fetchImageBlob();
        const mimeType = blob.type || 'image/jpeg';
        const ext = mimeType.includes('png') ? 'png' : 'jpg';
        const file = new File(
            [blob],
            this.getImageFilename(ext),
            {type: mimeType},
        );

        const shareDataWithFile = {
          title: 'AMII es IA',
          text: this.shareDescription,
          files: [file],
        };

        if (
          navigator.canShare
          && navigator.canShare({files: [file]})
          && navigator.share
        ) {
          await navigator.share(shareDataWithFile);
          return;
        }

        if (navigator.share) {
          await navigator.share({
            title: 'AMII es IA',
            text: `${this.shareDescription} ${window.location.href}`,
            url: window.location.href,
          });
          return;
        }

        window.open(this.whatsappShareUrl, '_blank', 'noopener,noreferrer');
      } catch (err) {
        if (err && err.name === 'AbortError') {
          // El usuario canceló el diálogo nativo de compartir
          return;
        }
        console.warn('[AmiiEsIA] Error compartiendo imagen:', err);
        // Fallback a WhatsApp directo si falla Web Share API
        window.open(this.whatsappShareUrl, '_blank', 'noopener,noreferrer');
      } finally {
        this.sharing = false;
      }
    },

    async downloadImage() {
      if (!this.imageUrl || this.downloading) return;
      this.downloading = true;
      this.actionMessage = '';

      try {
        const blob = await this.fetchImageBlob();
        const mimeType = blob.type || 'image/jpeg';
        const ext = mimeType.includes('png') ? 'png' : 'jpg';
        const blobUrl = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = blobUrl;
        link.download = this.getImageFilename(ext);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(blobUrl);
      } catch (err) {
        console.warn('[AmiiEsIA] Error al descargar imagen:', err);
        // Fallback: intentar descarga directa mediante enlace
        const link = document.createElement('a');
        link.href = this.imageUrl;
        link.download = this.getImageFilename();
        link.target = '_blank';
        link.rel = 'noopener noreferrer';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      } finally {
        this.downloading = false;
      }
    },

    async loadImage() {
      this.loading = true;
      try {
        const {data} = await this.$apollo.query({
          query: PUBLIC_IMAGE_QUERY,
          variables: {id: this.imageId},
          fetchPolicy: 'no-cache',
        });
        const img = data.publicEventImage;
        if (img && img.url) {
          this.imageUrl = `${this.eventImagesRoot}${img.url}`;
          this.specialty = img.specialty || '';
        }
      } catch (err) {
        console.warn('[AmiiEsIA] Error cargando la imagen:', err);
      } finally {
        this.loading = false;
      }
    },

    async submitLead() {
      if (!this.lead.name && !this.lead.whatsapp) {
        this.leadError = 'Ingresá al menos tu nombre o WhatsApp.';
        return;
      }
      this.sendingLead = true;
      this.leadError = '';
      try {
        const {data} = await this.$apollo.mutate({
          mutation: LEAD_MUTATION,
          variables: {
            imageId: this.imageId,
            lead: {
              name: this.lead.name || null,
              phone: this.lead.whatsapp || null,
              instagram: this.lead.instagram || null,
              specialty: this.specialty || null,
            },
          },
        });
        const res = data.eventImageLeadSubmit;
        if (!res || res.msg !== 'success') {
          this.leadError = 'No se pudo enviar. Intentá de nuevo.';
          return;
        }
        this.leadSent = true;
      } catch (err) {
        this.leadError = 'No se pudo enviar. Intentá de nuevo.';
      } finally {
        this.sendingLead = false;
      }
    },
  },
};
</script>
